import type { ReactNode } from 'react';

import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';

// One notification row: title + body + timestamp, and an optional trailing
// control (a mark-read toggle on the member side, a dismiss on the CAM side).
// This molecule owns only its internal layout, via system props. Shared by the
// member Notification Centre and the CAM-admin notifications feed so both read
// the same.
//
// No type icon. It was a 40px tinted circle down the left of every row, which at
// four types read as a column of coloured discs rather than as information: the
// title already says what kind of thing happened, and a notification nobody can
// act on differently for its type does not need to be sorted by eye. Dropping it
// also gives the title back the width it was clipping in.
//
// The row is a CardActionArea rather than a Card with an onClick, which is what
// gets it hover, pressed and keyboard-focus states from the theme instead of a
// pile of per-instance sx. It also makes the row a real button, so it is
// reachable by tab and answers the space bar. The trailing control sits outside
// the action area, because a button inside a button is invalid markup and the
// envelope has its own job.
const NotificationRow = ({
  title,
  body,
  when,
  read,
  onOpen,
  trailing,
}: {
  title: string;
  body: string;
  when: string;
  read: boolean;
  onOpen: () => void;
  // The trailing action — its click is stopped from reaching the card.
  trailing?: ReactNode;
}) => {
  return (
    // The action area spans the whole row and the trailing control floats on top
    // of it, rather than sitting beside it as a sibling. Side by side, the hover
    // highlight stopped at the button's column and the row read as notched. This
    // way one hover covers the row, and the button is still its own element rather
    // than a button nested inside a button.
    // Box rather than sx on the Card: Card is not a system-props component, and
    // the positioned ancestor only has to wrap the surface, not be it.
    <Box sx={{ position: 'relative' }}>
      <Card>
        <CardActionArea onClick={onOpen}>
          <Stack
            sx={{ alignItems: 'flex-start', p: 2 }}
            direction='row'
            spacing={2}
          >
            <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
              {/* The time runs inline, immediately after the last word of the
                  title rather than out at the end of the row. A flex child would
                  be pushed to the right edge the moment the title wrapped; a span
                  in the same text flow stays next to the words.
                  Same size as the title and a lighter colour: the variant sets its
                  own regular weight, so it does not pick up the semibold an unread
                  title carries, and colour alone is what holds it back.
                  The title itself wraps. It used to be noWrap, which on a phone
                  clipped it to about thirty characters and put the rest somewhere
                  nobody can reach: the row opens the thing the notification is
                  about, not the notification, so there is no screen where the full
                  title exists. */}
              <Typography variant='body1'>
                {title}
                <Typography
                  sx={{ ml: 1 }}
                  component='span'
                  variant='body1'
                  color='text.secondary'
                >
                  {when}
                </Typography>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {body}
              </Typography>
            </Stack>

            {/* Room for the floating control, so a long title never runs under it. */}
            {trailing && <Box sx={{ width: 40, flexShrink: 0 }} />}
          </Stack>
        </CardActionArea>
      </Card>

      {trailing && (
        <Box
          sx={{ position: 'absolute', top: 0, right: 0, pt: 2, pr: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {trailing}
        </Box>
      )}
    </Box>
  );
};

NotificationRow.displayName = 'NotificationRow';
export default NotificationRow;
